// client/src/components/LoadingSpinner.js
import React from 'react';

// Inline spinner for use within buttons or text
export function InlineSpinner({ text = '', color = '#6b7280' }) {
  return (
    <span style={{ 
      display: 'inline-flex', 
      alignItems: 'center',
      gap: '6px'
    }}>
      <span style={{
        display: 'inline-block',
        width: '14px',
        height: '14px',
        border: `2px solid ${color}`,
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.6s linear infinite'
      }}></span>
      {text && <span>{text}</span>}
    </span>
  );
}

// Button spinner - CartSmash themed
export function ButtonSpinner({ color = '#fff' }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      width: '16px',
      height: '16px'
    }}>
      <span style={{ 
        fontSize: '12px',
        animation: 'cartsmash-thinking 1.5s ease-in-out infinite'
      }}>🛒</span>
      
      {/* Mini floating grocery */}
      <span style={{
        position: 'absolute',
        top: '-6px',
        right: '-2px',
        fontSize: '4px',
        animation: 'cartsmash-grocery-float 2s ease-in-out infinite'
      }}>🥕</span>
    </span>
  );
}

// Full overlay spinner - mobile optimized
export function OverlaySpinner({ text = 'Loading...' }) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0, // Shorthand for top: 0, right: 0, bottom: 0, left: 0
      width: '100%',
      height: '100%',
      minHeight: '-webkit-fill-available', // iOS Safari fix
      backgroundColor: 'rgba(255,255,255,0.95)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      gap: '16px',
      overflow: 'hidden', // Prevent any overflow
      margin: 0,
      padding: '20px',
      boxSizing: 'border-box',
      touchAction: 'none', // Prevent scroll on touch
      userSelect: 'none',
      WebkitUserSelect: 'none',
      msUserSelect: 'none'
    }}>
      <div style={{
        width: '48px',
        height: '48px',
        border: '4px solid transparent',
        borderRadius: '6px',
        animation: 'overlay-square-rotate 1.2s linear infinite, overlay-square-colors 2s linear infinite',
        flexShrink: 0
      }}></div>
      <div style={{
        fontSize: 'clamp(14px, 4vw, 18px)', // Responsive font size
        color: '#374151',
        fontWeight: '500',
        textAlign: 'center',
        maxWidth: '90%' // Prevent edge touching
      }}>{text}</div>
    </div>
  );
}

// Progress spinner with color cycling
export function ProgressSpinner({ text = 'Processing...' }) {
  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        border: '6px solid transparent',
        borderRadius: '8px',
        animation: 'progress-square-rotate 1.5s linear infinite, progress-square-colors 2.5s linear infinite',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}></div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '16px', color: '#374151', fontWeight: '500' }}>
          {text}
        </div>
      </div>
    </div>
  );
}

// Default loading spinner - CartSmash themed
export default function LoadingSpinner({ text = 'Loading...', size = 'medium', color = '#FF6B35', inline = false }) {
  const sizes = {
    small: { cart: '16px', groceries: '6px', font: '12px', gap: '8px', groceryOffset: '12px' },
    medium: { cart: '24px', groceries: '8px', font: '14px', gap: '12px', groceryOffset: '18px' },
    large: { cart: '32px', groceries: '10px', font: '16px', gap: '16px', groceryOffset: '24px' }
  };

  const currentSize = sizes[size] || sizes.medium;

  const containerStyle = inline ? {
    display: 'inline-flex',
    alignItems: 'center',
    gap: currentSize.gap
  } : {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: currentSize.gap,
    padding: '12px'
  };

  return (
    <div style={containerStyle}>
      {/* CartSmash grocery cart with floating items */}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: currentSize.cart,
        height: currentSize.cart,
        animation: 'cartsmash-thinking 1.5s ease-in-out infinite'
      }}>
        <div style={{ fontSize: currentSize.cart }}>🛒</div>
        
        {/* Floating grocery items */}
        <div style={{
          position: 'absolute',
          top: `-${currentSize.groceryOffset}`,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: currentSize.groceries,
          animation: 'cartsmash-grocery-float 2s ease-in-out infinite',
          animationDelay: '0s'
        }}>🥕</div>
        
        <div style={{
          position: 'absolute',
          right: `-${currentSize.groceryOffset}`,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: currentSize.groceries,
          animation: 'cartsmash-grocery-float 2s ease-in-out infinite',
          animationDelay: '0.5s'
        }}>🍎</div>
        
        <div style={{
          position: 'absolute',
          bottom: `-${currentSize.groceryOffset}`,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: currentSize.groceries,
          animation: 'cartsmash-grocery-float 2s ease-in-out infinite',
          animationDelay: '1s'
        }}>🥛</div>
        
        <div style={{
          position: 'absolute',
          left: `-${currentSize.groceryOffset}`,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: currentSize.groceries,
          animation: 'cartsmash-grocery-float 2s ease-in-out infinite',
          animationDelay: '1.5s'
        }}>🍞</div>
      </div>
      
      {text && (
        <div style={{
          fontSize: currentSize.font,
          color: color,
          fontWeight: '500',
          textAlign: 'center'
        }}>{text}</div>
      )}
    </div>
  );
}

// Add CSS animation if not already present
if (typeof document !== 'undefined' && !document.querySelector('#spinner-keyframes')) {
  const style = document.createElement('style');
  style.id = 'spinner-keyframes';
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes cartsmash-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes cartsmash-thinking {
      0%, 100% { transform: rotate(0deg) scale(1); }
      25% { transform: rotate(-3deg) scale(1.05); }
      50% { transform: rotate(3deg) scale(1.1); }
      75% { transform: rotate(-2deg) scale(1.05); }
    }
    
    @keyframes cartsmash-grocery-float {
      0%, 100% { 
        transform: translateX(-50%) translateY(0px) rotate(0deg); 
        opacity: 0.7; 
      }
      33% { 
        transform: translateX(-50%) translateY(-6px) rotate(120deg); 
        opacity: 1; 
      }
      66% { 
        transform: translateX(-50%) translateY(-3px) rotate(240deg); 
        opacity: 0.8; 
      }
    }
    
    @keyframes cartsmash-colors {
      0% { stroke: #FF6B35; }
      25% { stroke: #F7931E; }
      50% { stroke: #FF6B35; }
      75% { stroke: #F7931E; }
      100% { stroke: #FF6B35; }
    }
    
    @keyframes overlay-square-rotate {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes overlay-square-colors {
      0% { 
        border-color: #FF6B35 transparent transparent transparent; 
        box-shadow: 0 0 10px rgba(255, 107, 53, 0.6); 
      }
      20% { 
        border-color: #F7931E transparent transparent transparent; 
        box-shadow: 0 0 10px rgba(247, 147, 30, 0.6); 
      }
      40% { 
        border-color: #28a745 transparent transparent transparent; 
        box-shadow: 0 0 10px rgba(40, 167, 69, 0.6); 
      }
      60% { 
        border-color: #007bff transparent transparent transparent; 
        box-shadow: 0 0 10px rgba(0, 123, 255, 0.6); 
      }
      80% { 
        border-color: #dc3545 transparent transparent transparent; 
        box-shadow: 0 0 10px rgba(220, 53, 69, 0.6); 
      }
      100% { 
        border-color: #FF6B35 transparent transparent transparent; 
        box-shadow: 0 0 10px rgba(255, 107, 53, 0.6); 
      }
    }
    
    @keyframes progress-square-rotate {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes progress-square-colors {
      0% { 
        border-color: #FF6B35 transparent transparent transparent; 
        box-shadow: 0 0 15px rgba(255, 107, 53, 0.4); 
      }
      16% { 
        border-color: #F7931E transparent transparent transparent; 
        box-shadow: 0 0 15px rgba(247, 147, 30, 0.4); 
      }
      32% { 
        border-color: #28a745 transparent transparent transparent; 
        box-shadow: 0 0 15px rgba(40, 167, 69, 0.4); 
      }
      48% { 
        border-color: #007bff transparent transparent transparent; 
        box-shadow: 0 0 15px rgba(0, 123, 255, 0.4); 
      }
      64% { 
        border-color: #dc3545 transparent transparent transparent; 
        box-shadow: 0 0 15px rgba(220, 53, 69, 0.4); 
      }
      80% { 
        border-color: #6f42c1 transparent transparent transparent; 
        box-shadow: 0 0 15px rgba(111, 66, 193, 0.4); 
      }
      100% { 
        border-color: #FF6B35 transparent transparent transparent; 
        box-shadow: 0 0 15px rgba(255, 107, 53, 0.4); 
      }
    }
  `;
  document.head.appendChild(style);
}

// Mobile-optimized full screen loading screen
export function MobileLoadingScreen({ text = 'Initializing AI-powered grocery parsing...' }) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0, // Shorthand for top: 0, right: 0, bottom: 0, left: 0
      width: '100%',
      height: '100%',
      minHeight: '-webkit-fill-available', // iOS Safari fix
      background: 'linear-gradient(135deg, #ff7043 0%, #ff9558 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden', // Prevent any overflow
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      touchAction: 'none', // Prevent scroll on touch
      userSelect: 'none',
      WebkitUserSelect: 'none',
      msUserSelect: 'none'
    }}>
      {/* Logo and Brand */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '60px',
        padding: '0 20px', // Add horizontal padding for mobile
        maxWidth: '100%', // Ensure it doesn't exceed viewport
      }}>
        <span style={{
          fontSize: '48px',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
        }}>
          ⭐
        </span>
        <h1 style={{
          color: 'white',
          fontSize: 'clamp(28px, 8vw, 48px)', // Responsive font size
          fontWeight: 'bold',
          margin: 0,
          letterSpacing: '-1px',
          textShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          CARTSMASH
        </h1>
      </div>

      {/* Tagline */}
      <p style={{
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: 'clamp(14px, 4vw, 18px)', // Responsive font size
        margin: '0 0 40px 0',
        padding: '0 20px', // Add padding for mobile
        textAlign: 'center',
        maxWidth: '90%' // Prevent edge touching
      }}>
        {text}
      </p>

      {/* Loading Spinner */}
      <div style={{
        width: '50px',
        height: '50px',
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderTop: '3px solid white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
    </div>
  );
}